import { TRAVEL_QUOTE_STEP_KEYS } from "@/D2C/features/travel-quote/common/static";
import { saveTravelQuoteStepFor } from "@/D2C/features/travel-quote/redux/travel-quote/travelQuoteSlice";
import {
  createTravelSession,
  updateTravelSessionStep,
} from "@/D2C/features/travel-quote/services/service";
import {
  buildTravelCreateSessionPayload,
  buildTravelQuoteRequestBody,
} from "@/D2C/features/travel-quote/utils/travelQuotePayloadsUtils";
import { App_Url_New, LOB_IDS, Roles } from "@/shared/common/static";
import { SESSION_STEP_KEYS } from "@/shared/constants/sessionStepKeys";
import { setSession } from "@/shared/redux/reducers/authSlice";
import { clearQuoteList, setQuoteList, sortQuotes } from "@/shared/redux/reducers/commonSlice";
import { setSharedPersonalDetails } from "@/shared/redux/reducers/personalDetailsSlice";
import type { AppDispatch } from "@/shared/redux/store/store";
import { usePosterReducers } from "@/shared/redux/store/useGetData";
import { streamQuotes } from "@/shared/services/quoteStreamService";
import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { getSessionIds, type SessionIds } from "../utils/getSessionIds";
import type { useQuoteRefs } from "./useQuoteRefs";

type Refs = ReturnType<typeof useQuoteRefs>;

export const useTravelQuotes = (
  refs: Pick<Refs, "travelPersonalRef" | "travelDetailsRef">,
  setActiveSessionIds: (ids: SessionIds) => void
) => {
  const dispatch = useDispatch<AppDispatch>();
  const { auth } = usePosterReducers();
  const isAgent = auth?.user?.user?.roles?.some(
    (role) => role.name?.trim().toLowerCase() === Roles.AGENT
  );
  const assignedUserId = isAgent ? (auth?.user?.user?.id ?? auth?.user?.id ?? null) : null;

  return useCallback(async () => {
    const personalValid = await refs.travelPersonalRef.current?.submit();
    const detailsValid = await refs.travelDetailsRef.current?.submit();
    if (!personalValid || !detailsValid) return false;

    const personalData = refs.travelPersonalRef.current!.getValues();
    const travelData = refs.travelDetailsRef.current!.getValues();

    const responseData = await createTravelSession({
      step: SESSION_STEP_KEYS.PERSONAL_DETAILS,
      ...buildTravelCreateSessionPayload(personalData),
      assigned_user_id: assignedUserId,
      assign_user_id: assignedUserId,
    });
    const ids = getSessionIds(responseData);
    setActiveSessionIds(ids);

    const requestBody = buildTravelQuoteRequestBody(personalData as any, travelData, ids.session);
    dispatch(setSession(responseData));
    dispatch(setSharedPersonalDetails(personalData as any));
    dispatch(saveTravelQuoteStepFor(TRAVEL_QUOTE_STEP_KEYS.PERSONAL_DETAILS, personalData));
    dispatch(saveTravelQuoteStepFor(TRAVEL_QUOTE_STEP_KEYS.TRAVEL_DETAILS, travelData));

    if (ids.clientId && ids.sessionId) {
      await updateTravelSessionStep({
        step: SESSION_STEP_KEYS.PRE_QUOTE_DETAILS,
        payload: {
          travel_details: requestBody.travel_details,
          personal_details: requestBody.personal_details,
        },
        start_date: travelData.startDate || null,
        end_date: travelData.endDate || null,
        clientId: ids.clientId,
        sessionId: ids.sessionId,
        lobId: LOB_IDS.TRAVEL,
      });
    }

    const collectedQuotes: any[] = [];
    dispatch(clearQuoteList());
    await streamQuotes({
      endpointPath: App_Url_New.ApiEndPoint.travelGetQuote,
      requestBody,
      onQuoteReceived: (quote) => {
        dispatch(setQuoteList(quote as any));
        dispatch(sortQuotes("sequence-asc"));
        if (quote?.insurer?.name && quote?.plan?.name) collectedQuotes.push(quote);
      },
      onStreamComplete: () => {
        if (!ids.clientId || !ids.sessionId || collectedQuotes.length === 0) return;
        void updateTravelSessionStep({
          step: "quote_request",
          payload: {
            travel_details: requestBody.travel_details,
            personal_details: requestBody.personal_details,
            quotes: collectedQuotes,
          },
          clientId: ids.clientId,
          sessionId: ids.sessionId,
          lobId: LOB_IDS.TRAVEL,
        });
      },
      onStreamError: (err) => console.error("Travel quote stream error:", err),
    });
    dispatch(sortQuotes("sequence-asc"));
    return true;
  }, [assignedUserId, dispatch, refs, setActiveSessionIds]);
};
